const STALE_APP_RELOAD_KEY = "mathgpt:stale-app-asset-reload-state";
const STALE_APP_MAX_AUTO_RELOADS = 1;

type StaleAppReloadState = {
  count: number;
  lastReloadAt: number;
};

const STALE_APP_ASSET_ERROR_PATTERNS = [
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /unable to preload css/i
];

export function isStaleAppAssetError(error: unknown) {
  const text = errorToSearchText(error);
  return STALE_APP_ASSET_ERROR_PATTERNS.some((pattern) =>
    pattern.test(text)
  );
}

export function recoverFromStaleAppAssetError(error: unknown) {
  if (!isStaleAppAssetError(error) || typeof window === "undefined") {
    return false;
  }

  if (!markStaleAppAssetReloadAttempt()) {
    return false;
  }

  window.location.reload();
  return true;
}

export function markStaleAppAssetReloadAttempt(
  now = Date.now(),
  storage: Pick<Storage, "getItem" | "setItem"> | null | undefined =
    getSessionStorage()
) {
  if (!storage) return false;

  try {
    const state = parseReloadState(storage.getItem(STALE_APP_RELOAD_KEY));
    if (state.count >= STALE_APP_MAX_AUTO_RELOADS) {
      return false;
    }
    storage.setItem(
      STALE_APP_RELOAD_KEY,
      JSON.stringify({ count: state.count + 1, lastReloadAt: now })
    );
  } catch {
    return false;
  }

  return true;
}

function parseReloadState(value: string | null): StaleAppReloadState {
  if (!value) return { count: 0, lastReloadAt: 0 };

  try {
    const parsed = JSON.parse(value) as Partial<StaleAppReloadState>;
    return {
      count: Number.isFinite(parsed.count) ? Number(parsed.count) : 0,
      lastReloadAt: Number.isFinite(parsed.lastReloadAt)
        ? Number(parsed.lastReloadAt)
        : 0
    };
  } catch {
    return { count: 0, lastReloadAt: 0 };
  }
}

function getSessionStorage() {
  if (typeof window === "undefined") return undefined;

  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function errorToSearchText(error: unknown) {
  if (error instanceof Error) {
    return [error.name, error.message, error.stack || ""].join("\n");
  }
  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}
